import { type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Network, Compass, Plus, Search, Bell, Settings, Sun, Moon, Github,
  Shield, User as UserIcon, LogOut, BookOpen, Sparkles,
} from "lucide-react";
import { useStore } from "@/store/useStore";
import { cn } from "@/lib/cn";
import { useToast } from "@/components/Toast";

export function AppShell() {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-strain-field flex flex-col">
      <TopBar />
      <div className="flex-1 flex">
        <SideNav pathname={pathname} />
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function TopBar() {
  const navigate = useNavigate();
  const { loggedIn, logout, user, theme, toggleTheme } = useStore();
  const toast = useToast((s) => s.push);

  return (
    <header className="sticky top-0 z-40 glass border-b border-[var(--color-border)]">
      <div className="h-14 px-4 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2 group">
          <Logo className="w-7 h-7" />
          <div className="leading-none">
            <div className="text-sm font-semibold tracking-tight">idea<span className="text-[var(--color-primary)]">strains</span></div>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-1 ml-4">
          <NavTab to="/app" icon={<Compass size={15} />} label="Feed" />
          <NavTab to="/search" icon={<Search size={15} />} label="Discover" />
          <NavTab to="/about" icon={<BookOpen size={15} />} label="How it works" />
        </div>

        <div className="flex-1" />

        <button
          onClick={toggleTheme}
          className="h-9 w-9 grid place-items-center rounded-lg text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)] transition-colors"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
        </button>

        <a
          href="https://github.com"
          target="_blank"
          rel="noreferrer"
          className="hidden sm:grid h-9 w-9 place-items-center rounded-lg text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)] transition-colors"
          aria-label="Source"
        >
          <Github size={16} />
        </a>

        {loggedIn && user ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/create")}
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[var(--color-primary)] text-[var(--color-void)] text-sm font-semibold hover:bg-[var(--color-primary-bright)] transition-colors"
            >
              <Plus size={15} /> New strain
            </button>
            <Avatar initials={user.avatar_initials ?? user.username.slice(0, 2).toUpperCase()} size={32} onClick={() => navigate(`/u/${user.username}`)} />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => { navigate("/login"); }}
              className="text-sm text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] px-2 h-9 rounded-lg hover:bg-[var(--color-surface-2)] transition-colors"
            >
              Log in
            </button>
            <button
              onClick={() => { useStore.getState().loginDemo().then(() => { navigate("/app"); toast({ tone: "ok", title: "Welcome", desc: "Backend session active." }); }); }}
              className="text-sm font-semibold h-9 px-3 rounded-lg bg-[var(--color-primary)] text-[var(--color-void)] hover:bg-[var(--color-primary-bright)] transition-colors"
            >
              Get started
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function NavTab({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm transition-colors",
          isActive
            ? "text-[var(--color-ink)] bg-[var(--color-surface-2)]"
            : "text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]",
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

function SideNav({ pathname }: { pathname: string }) {
  // slim left rail on desktop only — keeps the explorer maximally wide
  if (pathname.startsWith("/s/") || pathname === "/create") return null;

  const navigate = useNavigate();
  const items = [
    { to: "/app", icon: <Compass size={17} />, label: "Feed" },
    { to: "/search", icon: <Search size={17} />, label: "Discover" },
    { to: "/create", icon: <Plus size={17} />, label: "Create" },
    { to: "/notifications", icon: <Bell size={17} />, label: "Activity" },
    { to: "/u/you", icon: <UserIcon size={17} />, label: "Profile" },
    { to: "/settings", icon: <Settings size={17} />, label: "Settings" },
    { to: "/about", icon: <BookOpen size={17} />, label: "How it works" },
    { to: "/admin", icon: <Shield size={17} />, label: "Admin" },
  ];

  return (
    <nav className="hidden lg:flex w-[200px] shrink-0 border-r border-[var(--color-border)] p-3 flex-col gap-1 sticky top-14 h-[calc(100vh-3.5rem)]">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-2.5 h-9 px-3 rounded-lg text-sm transition-colors",
              isActive
                ? "bg-[var(--color-surface-2)] text-[var(--color-ink)]"
                : "text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]",
            )
          }
        >
          {it.icon}
          {it.label}
        </NavLink>
      ))}
      <div className="mt-auto p-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] text-xs">
        <div className="flex items-center gap-1.5 text-[var(--color-inferred)] mb-1.5">
          <Sparkles size={13} /> <span className="font-mono uppercase tracking-wider text-[10px]">prototype</span>
        </div>
        <p className="text-[var(--color-ink-muted)] leading-relaxed">
          Data is backed by SQLite. AI decomposition is real (gpt-5.5).
        </p>
      </div>
    </nav>
  );
}

export function Logo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="none" aria-hidden>
      <rect x="1" y="1" width="30" height="30" rx="8" fill="var(--color-surface)" stroke="var(--color-border)" />
      <circle cx="9" cy="16" r="3" fill="none" stroke="var(--color-primary)" strokeWidth="1.8" />
      <circle cx="9" cy="16" r="1.3" fill="var(--color-primary)" />
      <circle cx="20" cy="9" r="2.4" fill="none" stroke="var(--color-support)" strokeWidth="1.5" />
      <circle cx="20" cy="23" r="2.4" fill="none" stroke="var(--color-rebut)" strokeWidth="1.5" />
      <circle cx="26" cy="16" r="2.4" fill="none" stroke="var(--color-undercut)" strokeWidth="1.5" />
      <path d="M12 15 L18 10" stroke="var(--color-support)" strokeWidth="1.3" />
      <path d="M12 17 L18 22" stroke="var(--color-rebut)" strokeWidth="1.3" />
      <path d="M22.5 16 L24 16" stroke="var(--color-undercut)" strokeWidth="1.3" strokeDasharray="2 2" />
    </svg>
  );
}

export function Avatar({ initials, size = 32, onClick }: { initials: string; size?: number; onClick?: () => void }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
      className="rounded-full grid place-items-center font-mono font-semibold text-[var(--color-primary)] bg-[rgba(94,234,212,0.1)] border border-[rgba(94,234,212,0.25)] shrink-0"
    >
      {initials}
    </Comp>
  );
}

export function Spinner({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={cn("animate-spin", className)}
      aria-label="Loading"
    >
      <circle cx="12" cy="12" r="9" stroke="var(--color-border-bright)" strokeWidth="2.5" opacity="0.4" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function EmptyState({
  icon, title, desc, action,
}: { icon?: ReactNode; title: string; desc?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      {icon && <div className="text-[var(--color-ink-faint)] mb-4">{icon}</div>}
      <h3 className="text-base font-semibold text-[var(--color-ink)]">{title}</h3>
      {desc && <p className="text-sm text-[var(--color-ink-muted)] mt-1 max-w-sm">{desc}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
